type CarrierErr = { code?: string; message?: string };

const isRecord = (value: unknown): value is Record<string, unknown> =>
  value != null && typeof value === 'object' && !Array.isArray(value);

/** BioCair CreateShipment validation when temperature ≠ packaging. */
const BIOCAIR_TEMP_PACK_MISMATCH =
  /TemperatureId:\s*(\d+)\s+is invalid for chosen PackagingTypeId:\s*(\d+)/i;

const humanizeBioCairTempPackMismatchLine = (line: string): string => {
  const match = line.match(BIOCAIR_TEMP_PACK_MISMATCH);
  if (!match) return line;
  return (
    `Temperature is not valid for the selected packaging ` +
    `(BioCair temperature id ${match[1]} ≠ packaging id ${match[2]}). ` +
    `Pick a temperature allowed for that packaging.`
  );
};

/** Rewrite mismatch lines only; keep other carrier rejection messages. */
const humanizeBioCairTempPackMismatch = (message: string): string =>
  message
    .split('\n')
    .map((line) => humanizeBioCairTempPackMismatchLine(line))
    .join('\n');

const formatCarrierErrors = (errors: unknown): string | undefined => {
  if (!Array.isArray(errors) || errors.length === 0) return undefined;
  const lines = (errors as CarrierErr[])
    .map((entry) => {
      if (!isRecord(entry)) return '';
      const code = typeof entry.code === 'string' ? entry.code : '';
      const message = typeof entry.message === 'string' ? entry.message : '';
      return `${code}: ${message}`.replace(/^:\s*/, '').trim();
    })
    .filter(Boolean);
  return lines.length ? lines.join('\n') : undefined;
};

const detailsErrors = (details: unknown): string | undefined => {
  if (!isRecord(details)) return undefined;
  const nested = isRecord(details.response)
    ? details.response.errors
    : undefined;
  return formatCarrierErrors(details.errors) ?? formatCarrierErrors(nested);
};

const stringMessage = (value: unknown): string | undefined =>
  typeof value === 'string' && value.trim() ? value.trim() : undefined;

/**
 * Quote/book clients `reject(error.response)`, and ZoomLogi wraps CustomResponse
 * with `return_class`, so the carrier message lives at `data.data.message`
 * (and `data.data.data.error` / `details`).
 */
export const formatQuoteApiError = (err: unknown): string => {
  if (!err || typeof err !== 'object') return 'Unknown error';
  const e = err as { response?: { data?: unknown }; data?: unknown };
  const root = isRecord(e.response?.data)
    ? e.response.data
    : isRecord(e.data)
      ? e.data
      : isRecord(err)
        ? err
        : undefined;
  if (!root) return 'Request failed';

  const envelope = isRecord(root.data) ? root.data : root;
  const payload = isRecord(envelope.data) ? envelope.data : envelope;

  const raw =
    detailsErrors(payload.details) ??
    detailsErrors(envelope.details) ??
    stringMessage(payload.error) ??
    stringMessage(envelope.error) ??
    stringMessage(envelope.message) ??
    stringMessage(root.message) ??
    'Request failed';
  return humanizeBioCairTempPackMismatch(raw);
};
